import { useMemo, useState, useEffect, useRef } from 'react';
import { useAuth, signOut } from '../../../hooks/useAuth';
import { fetchMasteryFromDB, MasteryRecord, fetchMasteryHistoryFromDB, MasteryHistoryRecord } from '../../../hooks/useQuizMasteryDB';
import AuthModal from '../../../components/feature/AuthModal';

interface QuizQuestion {
  id: string;
  subject: string;
  subcategory: string | null;
}

interface MasteryChartProps {
  questions: QuizQuestion[];
  refreshKey?: number;
}

interface SubjectStats {
  subject: string;
  total: number;
  once: number;
  mastered: number;
  subcategories: SubcategoryStats[];
}

interface SubcategoryStats {
  name: string;
  total: number;
  once: number;
  mastered: number;
}

interface TimeSeriesDataPoint {
  date: string;
  correctRate: number;
  totalAnswers: number;
  correctAnswers: number;
}

interface SubjectTimeSeriesData {
  subject: string;
  data: TimeSeriesDataPoint[];
  color: string;
}

const STORAGE_KEY_COLLAPSED = 'nagayama_mastery_collapsed';

type ViewMode = 'overview' | 'trend';
type PeriodFilter = '7days' | '30days' | 'all';

function useAnimatedValue(target: number, duration: number = 800): number {
  const [value, setValue] = useState(target);
  const prevRef = useRef(target);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const from = prevRef.current;
    const to = target;
    prevRef.current = target;
    if (from === to) {
      setValue(to);
      return;
    }
    const startTime = performance.now();
    const animate = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(from + (to - from) * eased));
      if (progress < 1) rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [target, duration]);

  return value;
}

function AnimatedBar({
  masteredPct,
  oncePct,
  height = 'h-5',
}: {
  masteredPct: number;
  oncePct: number;
  height?: string;
}) {
  const [animM, setAnimM] = useState(0);
  const [animO, setAnimO] = useState(0);
  const prevM = useRef(0);
  const prevO = useRef(0);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const fromM = prevM.current;
    const fromO = prevO.current;
    prevM.current = masteredPct;
    prevO.current = oncePct;
    const startTime = performance.now();
    const duration = 900;
    const animate = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setAnimM(fromM + (masteredPct - fromM) * eased);
      setAnimO(fromO + (oncePct - fromO) * eased);
      if (progress < 1) rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [masteredPct, oncePct]);

  return (
    <div className={`w-full ${height} bg-gray-100 rounded-full overflow-hidden flex`}>
      {animM > 0.1 && (
        <div
          className="h-full bg-gradient-to-r from-teal-400 to-emerald-500"
          style={{ width: `${animM}%` }}
        ></div>
      )}
      {animO > 0.1 && (
        <div className="h-full bg-gray-300" style={{ width: `${animO}%` }}></div>
      )}
    </div>
  );
}

export default function MasteryChart({ questions, refreshKey }: MasteryChartProps) {
  const { user, loading: authLoading } = useAuth();
  const [mastery, setMastery] = useState<MasteryRecord>({});
  const [historyData, setHistoryData] = useState<MasteryHistoryRecord[]>([]);
  const [loadingMastery, setLoadingMastery] = useState(false);
  const [expandedSubject, setExpandedSubject] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(STORAGE_KEY_COLLAPSED) === 'true'
  );
  const [showAuth, setShowAuth] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('overview');
  const [periodFilter, setPeriodFilter] = useState<PeriodFilter>('30days');
  const [selectedSubject, setSelectedSubject] = useState<string | null>(null);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_COLLAPSED, String(collapsed));
  }, [collapsed]);

  // Fetch mastery from DB when user is logged in
  useEffect(() => {
    if (user) {
      setLoadingMastery(true);
      Promise.all([
        fetchMasteryFromDB(),
        fetchMasteryHistoryFromDB()
      ])
        .then(([record, history]) => {
          setMastery(record);
          setHistoryData(history);
        })
        .catch((err) => {
          console.error('Failed to fetch mastery:', err);
        })
        .finally(() => {
          setLoadingMastery(false);
        });
    } else {
      setMastery({});
      setHistoryData([]);
    }
  }, [user, refreshKey]);

  const stats = useMemo(() => {
    const subjectMap: Record<
      string,
      {
        total: number;
        once: number;
        mastered: number;
        subcatMap: Record<
          string,
          { total: number; once: number; mastered: number }
        >;
      }
    > = {};

    questions.forEach((q) => {
      if (!subjectMap[q.subject])
        subjectMap[q.subject] = { total: 0, once: 0, mastered: 0, subcatMap: {} };
      const s = subjectMap[q.subject];
      s.total++;
      const level = mastery[q.id]?.correct_count || 0;
      // 修正：1回以上正解で「学習中」、2回以上正解で「習熟済」
      if (level === 1) s.once++;
      else if (level >= 2) s.mastered++;

      if (q.subcategory) {
        if (!s.subcatMap[q.subcategory])
          s.subcatMap[q.subcategory] = { total: 0, once: 0, mastered: 0 };
        const sc = s.subcatMap[q.subcategory];
        sc.total++;
        if (level === 1) sc.once++;
        else if (level >= 2) sc.mastered++;
      }
    });

    return Object.entries(subjectMap)
      .map(([subject, data]) => ({
        subject,
        total: data.total,
        once: data.once,
        mastered: data.mastered,
        subcategories: Object.entries(data.subcatMap)
          .map(([name, sc]) => ({ name, ...sc }))
          .sort((a, b) => a.name.localeCompare(b.name)),
      }))
      .sort((a, b) => b.total - a.total) as SubjectStats[];
  }, [questions, mastery]);

  // 科目別の色を定義
  const subjectColors = useMemo(() => {
    const colors = [
      '#14b8a6', // teal
      '#10b981', // emerald
      '#3b82f6', // blue
      '#8b5cf6', // violet
      '#ec4899', // pink
      '#f59e0b', // amber
      '#ef4444', // red
      '#06b6d4', // cyan
      '#84cc16', // lime
      '#f97316', // orange
    ];
    const colorMap: Record<string, string> = {};
    stats.forEach((s, i) => {
      colorMap[s.subject] = colors[i % colors.length];
    });
    return colorMap;
  }, [stats]);

  // Build time series data for trend view - 大幅改善
  const timeSeriesData = useMemo(() => {
    if (historyData.length === 0 || questions.length === 0) return [];

    // Create a map of question_id to subject
    const questionSubjectMap: Record<string, string> = {};
    questions.forEach(q => {
      questionSubjectMap[q.id] = q.subject;
    });

    // Filter by period
    const now = new Date();
    let startDate = new Date(0);
    if (periodFilter === '7days') {
      startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    } else if (periodFilter === '30days') {
      startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    }

    const filteredHistory = historyData.filter(h => {
      const date = new Date(h.updated_at);
      return date >= startDate;
    });

    if (filteredHistory.length === 0) return [];

    // Group by subject and date - 正答率を正確に計算
    const subjectDateMap: Record<string, Record<string, { correct: number; total: number }>> = {};

    filteredHistory.forEach(h => {
      const subject = questionSubjectMap[h.question_id];
      if (!subject) return;

      const dateStr = new Date(h.updated_at).toISOString().split('T')[0];
      
      if (!subjectDateMap[subject]) subjectDateMap[subject] = {};
      if (!subjectDateMap[subject][dateStr]) {
        subjectDateMap[subject][dateStr] = { correct: 0, total: 0 };
      }

      subjectDateMap[subject][dateStr].total++;
      // is_correctフラグを使用して正答を判定
      if (h.is_correct) {
        subjectDateMap[subject][dateStr].correct++;
      }
    });

    // Convert to time series format with cumulative data
    const result: SubjectTimeSeriesData[] = [];
    
    Object.entries(subjectDateMap).forEach(([subject, dateData]) => {
      const dataPoints: TimeSeriesDataPoint[] = Object.entries(dateData)
        .map(([date, stats]) => ({
          date,
          correctRate: stats.total > 0 ? Math.round((stats.correct / stats.total) * 100) : 0,
          totalAnswers: stats.total,
          correctAnswers: stats.correct,
        }))
        .sort((a, b) => a.date.localeCompare(b.date));

      result.push({ 
        subject, 
        data: dataPoints,
        color: subjectColors[subject] || '#14b8a6',
      });
    });

    return result.sort((a, b) => b.data.length - a.data.length);
  }, [historyData, questions, periodFilter, subjectColors]);

  const overallTotal = questions.length;
  const overallOnce = useMemo(
    () => questions.filter((q) => (mastery[q.id]?.correct_count || 0) === 1).length,
    [questions, mastery]
  );
  const overallMastered = useMemo(
    () => questions.filter((q) => (mastery[q.id]?.correct_count || 0) >= 2).length,
    [questions, mastery]
  );
  const overallUntouched = overallTotal - overallOnce - overallMastered;
  const overallPctTarget =
    overallTotal > 0
      ? Math.round(((overallMastered + overallOnce) / overallTotal) * 100)
      : 0;

  const animatedOverallPct = useAnimatedValue(overallPctTarget, 900);
  const animatedMastered = useAnimatedValue(overallMastered, 900);
  const animatedOnce = useAnimatedValue(overallOnce, 900);
  const animatedUntouched = useAnimatedValue(overallUntouched, 900);

  const overallMasteredPct =
    overallTotal > 0 ? (overallMastered / overallTotal) * 100 : 0;
  const overallOncePct = overallTotal > 0 ? (overallOnce / overallTotal) * 100 : 0;

  if (questions.length === 0) return null;

  // Not logged in - show login prompt
  if (!authLoading && !user) {
    return (
      <>
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-6 text-center">
            <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-teal-400 to-emerald-500 rounded-full mx-auto mb-3">
              <i className="ri-bar-chart-box-line text-xl text-white"></i>
            </div>
            <h3 className="text-sm font-bold text-gray-800 mb-1">学習習熟度</h3>
            <p className="text-xs text-gray-500 mb-4">
              習熟度の記録・表示にはログインが必要です。<br />
              ログインすると、あなた専用の学習進捗を管理できます。
            </p>
            <button
              onClick={() => setShowAuth(true)}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-teal-500 to-emerald-600 text-white rounded-lg text-sm font-medium hover:from-teal-600 hover:to-emerald-700 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-login-box-line text-base"></i>
              ログインして習熟度を記録
            </button>
          </div>
        </div>
        {showAuth && (
          <AuthModal onClose={() => setShowAuth(false)} onSuccess={() => setShowAuth(false)} />
        )}
      </>
    );
  }

  if (authLoading || loadingMastery) {
    return <div className="bg-white rounded-xl border border-gray-200 p-6 animate-pulse h-32"></div>;
  }

  // Collapsed view
  if (collapsed) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div
          className="px-5 py-3 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors"
          onClick={() => setCollapsed(false)}
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center bg-gradient-to-br from-teal-400 to-emerald-500 rounded-lg shrink-0">
              <i className="ri-bar-chart-box-line text-sm text-white"></i>
            </div>
            <span className="text-sm font-bold text-gray-800">学習習熟度</span>
            <span className="text-xs font-bold text-transparent bg-clip-text bg-gradient-to-r from-teal-500 to-emerald-600">
              {animatedOverallPct}%
            </span>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-32 hidden sm:block">
              <AnimatedBar masteredPct={overallMasteredPct} oncePct={overallOncePct} height="h-2" />
            </div>
            <button className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-200 transition-colors cursor-pointer">
              <i className="ri-arrow-down-s-line text-lg text-gray-400"></i>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 border-b border-gray-100">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center bg-gradient-to-br from-teal-400 to-emerald-500 rounded-xl shrink-0">
              <i className="ri-bar-chart-box-line text-lg text-white"></i>
            </div>
            <div>
              <h3 className="text-sm font-bold text-gray-800">学習習熟度</h3>
              <p className="text-xs text-gray-400">1回正解=学習中（グレー）/ 2回以上正解=習熟済（緑）</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={async () => {
                await signOut();
              }}
              className="px-2.5 py-1.5 text-xs text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              title="ログアウト"
            >
              <i className="ri-logout-box-r-line text-sm mr-1"></i>
              ログアウト
            </button>
            <button
              onClick={() => setCollapsed(true)}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
              title="最小化"
            >
              <i className="ri-arrow-up-s-line text-lg text-gray-400"></i>
            </button>
          </div>
        </div>

        {/* View mode tabs */}
        <div className="flex items-center gap-2 mb-3">
          <button
            onClick={() => setViewMode('overview')}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap ${
              viewMode === 'overview'
                ? 'bg-gradient-to-r from-teal-500 to-emerald-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <i className="ri-bar-chart-fill text-sm mr-1"></i>
            習熟度一覧
          </button>
          <button
            onClick={() => setViewMode('trend')}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap ${
              viewMode === 'trend'
                ? 'bg-gradient-to-r from-teal-500 to-emerald-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <i className="ri-line-chart-line text-sm mr-1"></i>
            科目別正答率推移
          </button>
        </div>

        {viewMode === 'overview' && (
          <>
            <div className="flex items-center gap-4 mt-3">
              <div className="flex-1">
                <AnimatedBar masteredPct={overallMasteredPct} oncePct={overallOncePct} height="h-5" />
              </div>
              <span className="text-xs font-bold text-transparent bg-clip-text bg-gradient-to-r from-teal-500 to-emerald-600 whitespace-nowrap">
                {animatedOverallPct}%
              </span>
            </div>
            <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
              <span className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-gradient-to-r from-teal-400 to-emerald-500 inline-block"></span>
                習熟済 {animatedMastered}問
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-gray-300 inline-block"></span>
                学習中 {animatedOnce}問
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-gray-100 border border-gray-200 inline-block"></span>
                未学習 {animatedUntouched}問
              </span>
            </div>
          </>
        )}

        {viewMode === 'trend' && (
          <div className="mt-3">
            {/* Period filter */}
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xs text-gray-500">期間:</span>
              <button
                onClick={() => setPeriodFilter('7days')}
                className={`px-2.5 py-1 text-xs font-medium rounded-md transition-all cursor-pointer whitespace-nowrap ${
                  periodFilter === '7days'
                    ? 'bg-teal-100 text-teal-700'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                直近7日
              </button>
              <button
                onClick={() => setPeriodFilter('30days')}
                className={`px-2.5 py-1 text-xs font-medium rounded-md transition-all cursor-pointer whitespace-nowrap ${
                  periodFilter === '30days'
                    ? 'bg-teal-100 text-teal-700'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                直近30日
              </button>
              <button
                onClick={() => setPeriodFilter('all')}
                className={`px-2.5 py-1 text-xs font-medium rounded-md transition-all cursor-pointer whitespace-nowrap ${
                  periodFilter === 'all'
                    ? 'bg-teal-100 text-teal-700'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                全期間
              </button>
            </div>

            {/* Subject filter */}
            {timeSeriesData.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-gray-500">科目:</span>
                <button
                  onClick={() => setSelectedSubject(null)}
                  className={`px-2.5 py-1 text-xs font-medium rounded-md transition-all cursor-pointer whitespace-nowrap ${
                    selectedSubject === null
                      ? 'bg-teal-100 text-teal-700'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  すべて表示
                </button>
                {timeSeriesData.map(ts => (
                  <button
                    key={ts.subject}
                    onClick={() => setSelectedSubject(ts.subject)}
                    className={`px-2.5 py-1 text-xs font-medium rounded-md transition-all cursor-pointer whitespace-nowrap ${
                      selectedSubject === ts.subject
                        ? 'bg-teal-100 text-teal-700'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <span 
                      className="inline-block w-2 h-2 rounded-full mr-1.5" 
                      style={{ backgroundColor: ts.color }}
                    ></span>
                    {ts.subject}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Content area */}
      {viewMode === 'overview' && (
        <div className="divide-y divide-gray-100">
          {stats.map((s) => {
            const pctMastered = s.total > 0 ? (s.mastered / s.total) * 100 : 0;
            const pctOnce = s.total > 0 ? (s.once / s.total) * 100 : 0;
            const subjectPctTarget = s.total > 0 ? Math.round(((s.mastered + s.once) / s.total) * 100) : 0;
            const isExpanded = expandedSubject === s.subject;
            const hasSubcats = s.subcategories.length > 0;
            return (
              <SubjectRow
                key={s.subject}
                subject={s.subject}
                total={s.total}
                mastered={s.mastered}
                pctMastered={pctMastered}
                pctOnce={pctOnce}
                subjectPct={subjectPctTarget}
                isExpanded={isExpanded}
                hasSubcats={hasSubcats}
                subcategories={s.subcategories}
                onToggle={() => {
                  if (hasSubcats) setExpandedSubject(isExpanded ? null : s.subject);
                }}
              />
            );
          })}
        </div>
      )}

      {viewMode === 'trend' && (
        <div className="p-5">
          {timeSeriesData.length === 0 ? (
            <div className="text-center py-8">
              <div className="w-12 h-12 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-3">
                <i className="ri-line-chart-line text-xl text-gray-400"></i>
              </div>
              <p className="text-sm text-gray-500">
                選択した期間に学習データがありません。<br />
                クイズに挑戦して学習記録を蓄積しましょう！
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {selectedSubject === null ? (
                // 複数科目を同時表示
                <MultiSubjectTrendChart data={timeSeriesData} />
              ) : (
                // 単一科目を詳細表示
                timeSeriesData
                  .filter(ts => ts.subject === selectedSubject)
                  .map(ts => (
                    <TrendChart key={ts.subject} subject={ts.subject} data={ts.data} color={ts.color} />
                  ))
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// 複数科目を同時に表示するグラフコンポーネント
function MultiSubjectTrendChart({ data }: { data: SubjectTimeSeriesData[] }) {
  const [animated, setAnimated] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setAnimated(true), 100);
    return () => clearTimeout(timer);
  }, []);

  if (data.length === 0) return null;

  // すべての日付を収集
  const allDates = Array.from(
    new Set(data.flatMap(ts => ts.data.map(d => d.date)))
  ).sort();

  if (allDates.length === 0) return null;

  const maxRate = 100;
  const minRate = 0;

  return (
    <div className="border border-gray-200 rounded-lg p-4">
      <div className="mb-4">
        <h4 className="text-sm font-bold text-gray-800 mb-2">科目別正答率推移（比較）</h4>
        <div className="flex items-center gap-3 flex-wrap">
          {data.map(ts => (
            <div key={ts.subject} className="flex items-center gap-1.5">
              <span 
                className="w-3 h-3 rounded-full" 
                style={{ backgroundColor: ts.color }}
              ></span>
              <span className="text-xs text-gray-600">{ts.subject}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="relative w-full h-64 bg-gradient-to-b from-gray-50 to-white rounded-lg overflow-hidden">
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-full">
          {/* Grid lines */}
          {[0, 25, 50, 75, 100].map(y => (
            <line
              key={y}
              x1="0"
              y1={y}
              x2="100"
              y2={y}
              stroke="#e5e7eb"
              strokeWidth="0.2"
              vectorEffect="non-scaling-stroke"
            />
          ))}

          {/* 各科目の折れ線 */}
          {data.map((ts, idx) => {
            // 日付ごとのデータポイントを作成
            const points = allDates.map((date, i) => {
              const dataPoint = ts.data.find(d => d.date === date);
              const x = (i / Math.max(allDates.length - 1, 1)) * 100;
              const y = dataPoint 
                ? 100 - ((dataPoint.correctRate - minRate) / (maxRate - minRate)) * 100
                : null;
              return { x, y, rate: dataPoint?.correctRate, date };
            });

            const validPoints = points.filter(p => p.y !== null);
            if (validPoints.length === 0) return null;

            const pathD = validPoints.map((p, i) => 
              `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`
            ).join(' ');

            return (
              <g key={ts.subject}>
                {/* Line */}
                <path
                  d={pathD}
                  fill="none"
                  stroke={ts.color}
                  strokeWidth="2"
                  vectorEffect="non-scaling-stroke"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  opacity={animated ? 0.8 : 0}
                  strokeDasharray={animated ? '0' : '200'}
                  strokeDashoffset={animated ? '0' : '200'}
                  style={{ transition: `all 1.2s ease-out ${idx * 0.1}s` }}
                />

                {/* Data points */}
                {validPoints.map((p, i) => (
                  <g key={i}>
                    <circle
                      cx={p.x}
                      cy={p.y!}
                      r="1.2"
                      fill="white"
                      stroke={ts.color}
                      strokeWidth="0.5"
                      vectorEffect="non-scaling-stroke"
                      opacity={animated ? 1 : 0}
                      style={{ transition: `opacity 0.5s ease-out ${idx * 0.1 + i * 0.05}s` }}
                    />
                    <title>{`${ts.subject} - ${p.date}: ${p.rate}%`}</title>
                  </g>
                ))}
              </g>
            );
          })}
        </svg>

        {/* Y-axis labels */}
        <div className="absolute left-0 top-0 bottom-0 w-8 flex flex-col justify-between py-1 text-xs text-gray-400 pointer-events-none">
          <span>{maxRate}%</span>
          <span>{minRate}%</span>
        </div>
      </div>

      {/* Date labels */}
      <div className="flex items-center justify-between mt-2 text-xs text-gray-400">
        <span>{allDates[0]}</span>
        {allDates.length > 1 && <span>{allDates[allDates.length - 1]}</span>}
      </div>
    </div>
  );
}

function TrendChart({ subject, data, color }: { subject: string; data: TimeSeriesDataPoint[]; color: string }) {
  const [animated, setAnimated] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setAnimated(true), 100);
    return () => clearTimeout(timer);
  }, []);

  if (data.length === 0) return null;

  const maxRate = Math.max(...data.map(d => d.correctRate), 100);
  const minRate = Math.min(...data.map(d => d.correctRate), 0);
  const range = maxRate - minRate;
  const padding = range * 0.1;
  const chartMax = Math.min(maxRate + padding, 100);
  const chartMin = Math.max(minRate - padding, 0);
  const chartRange = chartMax - chartMin || 1;

  const points = data.map((d, i) => {
    const x = (i / Math.max(data.length - 1, 1)) * 100;
    const y = 100 - ((d.correctRate - chartMin) / chartRange) * 100;
    return { x, y, rate: d.correctRate, date: d.date, total: d.totalAnswers, correct: d.correctAnswers };
  });

  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
  const areaD = `${pathD} L 100 100 L 0 100 Z`;

  const latestRate = data[data.length - 1]?.correctRate || 0;
  const trend = data.length >= 2 ? data[data.length - 1].correctRate - data[0].correctRate : 0;
  const totalAnswers = data.reduce((sum, d) => sum + d.totalAnswers, 0);
  const totalCorrect = data.reduce((sum, d) => sum + d.correctAnswers, 0);
  const overallRate = totalAnswers > 0 ? Math.round((totalCorrect / totalAnswers) * 100) : 0;

  return (
    <div className="border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span 
            className="w-3 h-3 rounded-full" 
            style={{ backgroundColor: color }}
          ></span>
          <h4 className="text-sm font-bold text-gray-800">{subject}</h4>
          <span className="text-xs font-bold text-transparent bg-clip-text bg-gradient-to-r from-teal-500 to-emerald-600">
            最新: {latestRate}%
          </span>
        </div>
        <div className="flex items-center gap-3">
          {trend !== 0 && (
            <div className={`flex items-center gap-1 text-xs font-medium ${trend > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
              <i className={`${trend > 0 ? 'ri-arrow-up-line' : 'ri-arrow-down-line'} text-sm`}></i>
              {Math.abs(trend)}%
            </div>
          )}
          <div className="text-xs text-gray-500">
            期間平均: {overallRate}%
          </div>
        </div>
      </div>

      <div className="relative w-full h-48 bg-gradient-to-b from-gray-50 to-white rounded-lg overflow-hidden">
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-full">
          {/* Grid lines */}
          {[0, 25, 50, 75, 100].map(y => (
            <line
              key={y}
              x1="0"
              y1={y}
              x2="100"
              y2={y}
              stroke="#e5e7eb"
              strokeWidth="0.2"
              vectorEffect="non-scaling-stroke"
            />
          ))}

          {/* Area fill */}
          <defs>
            <linearGradient id={`gradient-${subject}`} x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor={color} stopOpacity="0.3" />
              <stop offset="100%" stopColor={color} stopOpacity="0.05" />
            </linearGradient>
          </defs>
          <path
            d={areaD}
            fill={`url(#gradient-${subject})`}
            opacity={animated ? 1 : 0}
            style={{ transition: 'opacity 0.8s ease-out' }}
          />

          {/* Line */}
          <path
            d={pathD}
            fill="none"
            stroke={color}
            strokeWidth="2.5"
            vectorEffect="non-scaling-stroke"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeDasharray={animated ? '0' : '200'}
            strokeDashoffset={animated ? '0' : '200'}
            style={{ transition: 'stroke-dashoffset 1.2s ease-out' }}
          />

          {/* Data points */}
          {points.map((p, i) => (
            <g key={i}>
              <circle
                cx={p.x}
                cy={p.y}
                r="1.8"
                fill="white"
                stroke={color}
                strokeWidth="0.6"
                vectorEffect="non-scaling-stroke"
                opacity={animated ? 1 : 0}
                style={{ transition: `opacity 0.5s ease-out ${i * 0.1}s` }}
              />
              <title>{`${p.date}\n正答率: ${p.rate}%\n正解: ${p.correct}/${p.total}問`}</title>
            </g>
          ))}
        </svg>

        {/* Y-axis labels */}
        <div className="absolute left-0 top-0 bottom-0 w-10 flex flex-col justify-between py-1 text-xs text-gray-400 pointer-events-none">
          <span>{Math.round(chartMax)}%</span>
          <span>{Math.round(chartMin)}%</span>
        </div>
      </div>

      {/* Date labels */}
      <div className="flex items-center justify-between mt-2 text-xs text-gray-400">
        <span>{data[0]?.date}</span>
        {data.length > 1 && <span>{data[data.length - 1]?.date}</span>}
      </div>

      <div className="mt-3 flex items-center gap-4 text-xs text-gray-500">
        <span>データ数: {data.length}日分</span>
        <span>総解答数: {totalAnswers}問</span>
        <span>総正解数: {totalCorrect}問</span>
      </div>
    </div>
  );
}

function SubjectRow({
  subject,
  total,
  mastered,
  pctMastered,
  pctOnce,
  subjectPct,
  isExpanded,
  hasSubcats,
  subcategories,
  onToggle,
}: {
  subject: string;
  total: number;
  mastered: number;
  pctMastered: number;
  pctOnce: number;
  subjectPct: number;
  isExpanded: boolean;
  hasSubcats: boolean;
  subcategories: SubcategoryStats[];
  onToggle: () => void;
}) {
  const animatedPct = useAnimatedValue(subjectPct, 900);
  return (
    <div>
      <div
        className={`px-5 py-3 flex items-center gap-3 ${hasSubcats ? 'cursor-pointer hover:bg-gray-50' : ''} transition-colors`}
        onClick={onToggle}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5">
            <span className="text-xs font-semibold text-gray-700">{subject}</span>
            <span className="text-xs text-gray-400">{total}問</span>
            {hasSubcats && (
              <i
                className={`ri-arrow-down-s-line text-xs text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
              />
            )}
          </div>
          <AnimatedBar masteredPct={pctMastered} oncePct={pctOnce} height="h-3" />
        </div>
        <div className="text-right shrink-0 w-16">
          <span className="text-xs font-bold text-transparent bg-clip-text bg-gradient-to-r from-teal-500 to-emerald-600">
            {animatedPct}%
          </span>
          <div className="text-xs text-gray-400 leading-tight">
            {mastered}/{total}
          </div>
        </div>
      </div>
      {isExpanded && hasSubcats && (
        <div className="bg-gray-50 px-5 py-2 space-y-2">
          {subcategories.map((sc) => {
            const scPctM = sc.total > 0 ? (sc.mastered / sc.total) * 100 : 0;
            const scPctO = sc.total > 0 ? (sc.once / sc.total) * 100 : 0;
            const scPct = sc.total > 0 ? Math.round(((sc.mastered + sc.once) / sc.total) * 100) : 0;
            return (
              <SubcategoryRow
                key={sc.name}
                name={sc.name}
                total={sc.total}
                scPctMastered={scPctM}
                scPctOnce={scPctO}
                scPct={scPct}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

function SubcategoryRow({
  name,
  total,
  scPctMastered,
  scPctOnce,
  scPct,
}: {
  name: string;
  total: number;
  scPctMastered: number;
  scPctOnce: number;
  scPct: number;
}) {
  const animatedPct = useAnimatedValue(scPct, 900);
  return (
    <div className="flex items-center gap-3">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xs text-gray-500">{name}</span>
          <span className="text-xs text-gray-300">{total}問</span>
        </div>
        <AnimatedBar masteredPct={scPctMastered} oncePct={scPctOnce} height="h-2" />
      </div>
      <span className="text-xs font-medium text-gray-500 w-10 text-right shrink-0">{animatedPct}%</span>
    </div>
  );
}